/* @sravni/react-header version 0.2.43 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { User, Location, Logout } from '@sravni/react-icons';
import { useStyles, useBoolean, useAuthLoginLink, useAuthLogoutLink, useDebounceValue, useBrowserThemeDetector, safeInvoke, useMergedRef } from '@sravni/react-utils';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Avatar } from '@sravni/react-design-system/lib/Avatar';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Divider } from '@sravni/react-design-system/lib/Divider';
import { Layout } from '@sravni/react-design-system/lib/Layout';
import { Logo } from '@sravni/react-design-system/lib/Logo';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { RegionDialog } from '@sravni/react-region-dialog';
import { subscribeOnLoginSuccess } from '@sravni/utils/lib/auth';
import { defaultUserNavigation } from '../../../mocks/defaultUserNavigation.js';
import { MENU_DELAY } from '../../../constants/settings.js';
import { linksMap, domainsDefault } from '../../../constants/links.js';
import { decorateSearchUrlByLinksMap, generateSearchUrl } from '../../../utils/search.js';
import { getModeView } from '../../../utils/modes.js';
import { generateUrl } from '../../../utils/links.js';
import { getUserName } from '../../../utils/user.js';
import { useAnalyticEvents, EVENTS } from '../../../hooks/useAnalyticEvents.js';
import '../../Search/index.js';
import '../../ThemeSwitcher/index.js';
import DesktopNavigation from './DesktopNavigation.js';
import '../../InjectedIcon/index.js';
import '../../DownloadApp/index.js';
import modules_9a3b202d from '../styles/Header.module.less.js';
import InjectedIcon from '../../InjectedIcon/src/InjectedIcon.js';
import { DownloadApp } from '../../DownloadApp/src/DownloadApp.js';
import Search from '../../Search/src/Search.js';
import ThemeSwitcher from '../../ThemeSwitcher/src/ThemeSwitcher.js';

/**
 * @description Desktop header component.
 *
 * @component
 * @example
 * ```jsx
 * <DesktopHeader user={user} settings={settings} />
 * ```
 */
var DesktopHeader = React.forwardRef(function (props, ref) {
    var serviceURL = props.serviceURL, oidcHandlerPrefix = props.oidcHandlerPrefix, _a = props.settings, settings = _a === void 0 ? {} : _a, user = props.user, regions = props.regions, currentRegion = props.currentRegion, promo = props.promo, _b = props.mode, mode = _b === void 0 ? 'default' : _b, _c = props.enableTheme, enableTheme = _c === void 0 ? false : _c, _d = props.fixedBackground, fixedBackground = _d === void 0 ? false : _d, _e = props.showContentWidget, showContentWidget = _e === void 0 ? true : _e, className = props.className, domainsOutside = props.domains, linksMapOutside = props.linksMap, formatPath = props.formatPath, onOpen = props.onOpen, onClose = props.onClose, restProps = __rest(props, ["serviceURL", "oidcHandlerPrefix", "settings", "user", "regions", "currentRegion", "promo", "mode", "enableTheme", "fixedBackground", "showContentWidget", "className", "domains", "linksMap", "formatPath", "onOpen", "onClose"]);
    var cx = useStyles(modules_9a3b202d);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _f = __read(useBoolean(false), 2), visibleMainMenu = _f[0], setVisibleMainMenu = _f[1];
    var _g = __read(useBoolean(false), 2), visibleSearchMenu = _g[0], setVisibleSearchMenu = _g[1];
    var _h = __read(useBoolean(false), 2), visibleRegionDialog = _h[0], setVisibleRegionDialog = _h[1];
    var isFirstRender = React.useRef(true);
    var headerRef = React.useRef(null);
    var headerContainerRef = React.useRef(null);
    var userBlockRef = React.useRef(null);
    var userMenuRef = React.useRef(null);
    var userMenuArrowRef = React.useRef(null);
    var searchMenu = React.useRef(null);
    var authLoginLinkProps = useAuthLoginLink({ prefix: oidcHandlerPrefix, isNewWindow: true });
    var authLogoutLinkProps = useAuthLogoutLink({ prefix: oidcHandlerPrefix, isNewWindow: false });
    var visibleMainMenuDebounced = useDebounceValue(visibleMainMenu, MENU_DELAY);
    var visibleSearchMenuDebounced = useDebounceValue(visibleSearchMenu, MENU_DELAY);
    var linksMap$1 = React.useMemo(function () { return (__assign(__assign({}, linksMap), linksMapOutside)); }, [linksMapOutside]);
    var domains = React.useMemo(function () { return (__assign(__assign({}, domainsDefault), domainsOutside)); }, [domainsOutside]);
    var userNavigation = React.useMemo(function () { return settings.userMenu || defaultUserNavigation; }, [settings]);
    var searchUrl = React.useMemo(function () { return decorateSearchUrlByLinksMap(generateSearchUrl(domains), linksMap$1); }, [domains, linksMap$1]);
    var backdropClassName = cx('backdrop', {
        'backdrop--active': visibleMainMenuDebounced
    });
    var headerClassName = cx(className, 'container', {
        'container--active': fixedBackground || visibleMainMenuDebounced
    });
    var handleOpenSearchMenu = React.useCallback(function () {
        setVisibleSearchMenu.on();
        setVisibleMainMenu.on();
    }, []);
    var handleOpenMainMenu = React.useCallback(function () {
        setVisibleSearchMenu.off();
        setVisibleMainMenu.on();
    }, []);
    var handleCloseMenus = React.useCallback(function () {
        setVisibleMainMenu.off();
        setVisibleSearchMenu.off();
    }, []);
    var handleAuthLoginClick = React.useCallback(function () {
        var callback = function () { return window.location.reload(); };
        subscribeOnLoginSuccess(callback);
        pushAnalyticEvent(EVENTS.LOGIN);
    }, []);
    var handleLogoutClick = React.useCallback(function () {
        pushAnalyticEvent(EVENTS.LOGOUT);
    }, []);
    var handleLinkClick = React.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent(EVENTS.MENU_LINK_CLICK, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|" + label });
    }, []);
    var handleRegionDialogOpen = React.useCallback(function () {
        setVisibleRegionDialog.on();
        pushAnalyticEvent(EVENTS.REGION_DIALOG_CLICK, { eventLabel: "\u0412\u044B\u0437\u043E\u0432 \u043F\u043E\u043F\u0430\u043F\u0430" });
    }, []);
    var handleRegionDialogChange = React.useCallback(function (region) {
        pushAnalyticEvent(EVENTS.REGION_DIALOG_CLICK, { eventLabel: region.name });
    }, []);
    var handleThemeSwitcherClick = React.useCallback(function (currentTheme) {
        var _a, _b;
        (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.sravniDataLayer) === null || _b === void 0 ? void 0 : _b.push({ theme: currentTheme });
        pushAnalyticEvent(EVENTS.THEME_SWITCHER_CLICK, { eventLabel: "\u0422\u0435\u043C\u0430 - " + currentTheme });
    }, [pushAnalyticEvent]);
    var handleBrowserThemeDetectorChange = React.useCallback(function (currentTheme) {
        var _a, _b;
        (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.sravniDataLayer) === null || _b === void 0 ? void 0 : _b.push({ theme: currentTheme });
        pushAnalyticEvent(EVENTS.THEME_DETECTOR_CHANGE, { eventLabel: "\u0422\u0435\u043C\u0430 - " + currentTheme });
    }, [pushAnalyticEvent]);
    useBrowserThemeDetector({ enabled: enableTheme, onChange: handleBrowserThemeDetectorChange });
    var modeView = React.useMemo(function () { return getModeView(mode); }, [mode]);
    var userAvatar = React.useMemo(function () {
        if (user && user.sub) {
            return React.createElement(Avatar, { size: 36, src: "//f.sravni.ru/avatars/" + user.sub + ".svg" });
        }
        return (React.createElement(Icon, { size: 16, color: "gray", className: modules_9a3b202d.userIcon },
            React.createElement(User, null)));
    }, [user]);
    var desktopUserMenu = React.useMemo(function () {
        var topGroup = (userNavigation || []).slice(0, -1);
        var bottomGroup = (userNavigation || []).slice(-1);
        return (React.createElement("div", { ref: userMenuRef, className: cx('user-nav__menu-wrapper') },
            React.createElement("div", { ref: userMenuArrowRef, className: cx('user-nav__arrow') }),
            React.createElement("div", { className: cx('user-nav__menu') },
                topGroup.map(function (item) { return (React.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('user-nav__menu-item') },
                    React.createElement(Space, { align: "center", size: 12 },
                        !!item.iconSrc && (React.createElement(Icon, { size: 20, icon: React.createElement(InjectedIcon, { iconSrc: item.iconSrc }) })),
                        item.icon && !item.iconSrc ? (React.createElement(Icon, { size: 20, icon: React.createElement("img", { src: item.icon }) })) : null,
                        React.createElement(Text, { size: 16, strong: true }, item.text)))); }),
                React.createElement(Divider, { className: cx('user-nav__menu-divider') }),
                React.createElement(DownloadApp, null),
                React.createElement(Link, { color: "black", onClick: handleRegionDialogOpen, className: cx('user-nav__menu-item') },
                    React.createElement(Space, { align: "center", size: 12 },
                        React.createElement(Icon, { icon: React.createElement(Location, null) }),
                        React.createElement(Space, { direction: "vertical" },
                            React.createElement(Text, { size: 12, className: "h-color-D30" }, "\u041C\u043E\u0439 \u0433\u043E\u0440\u043E\u0434"),
                            React.createElement(Text, { size: 16, strong: true }, (currentRegion === null || currentRegion === void 0 ? void 0 : currentRegion.name) ? currentRegion.name : 'Выберите город')))),
                bottomGroup.map(function (item) { return (React.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('user-nav__menu-item') },
                    React.createElement(Space, { align: "center", size: 12 },
                        !!item.iconSrc && (React.createElement(Icon, { size: 20, icon: React.createElement(InjectedIcon, { iconSrc: item.iconSrc }) })),
                        item.icon && !item.iconSrc ? (React.createElement(Icon, { size: 20, icon: React.createElement("img", { src: item.icon }) })) : null,
                        React.createElement(Text, { size: 16, strong: true }, item.text)))); }),
                React.createElement(Divider, { className: cx('user-nav__menu-divider') }),
                React.createElement(Link, __assign({ color: "gray" }, authLogoutLinkProps, { className: cx('user-nav__menu-item'), onClick: handleLogoutClick }),
                    React.createElement(Space, { align: "center", size: 12 },
                        React.createElement(Icon, { icon: React.createElement(Logout, null) }),
                        React.createElement(Text, { size: 16, strong: true }, "\u0412\u044B\u0439\u0442\u0438"))))));
    }, [currentRegion, authLogoutLinkProps, userNavigation]);
    var desktopUserBlock = React.useMemo(function () {
        if (user) {
            var userName = getUserName(user);
            return (React.createElement(Space, { size: 16, align: "center" },
                modeView.showSearch && (React.createElement(Search, { ref: searchMenu, url: searchUrl, visible: visibleSearchMenuDebounced, onOpen: handleOpenSearchMenu, onClose: handleCloseMenus })),
                enableTheme && React.createElement(ThemeSwitcher, { onClick: handleThemeSwitcherClick }),
                modeView.showUser && (React.createElement(Space, { ref: userBlockRef, align: "center", size: 12, className: cx('user-nav') },
                    userAvatar,
                    React.createElement(Space, { direction: "vertical", align: "start" },
                        !!userName && React.createElement(Text, { size: 12, className: "h-color-D30" }, "\u041F\u0440\u043E\u0444\u0438\u043B\u044C"),
                        React.createElement(Text, { strong: true, nowrap: true, className: cx('userName'), title: userName || 'Ваш профиль' }, userName || 'Ваш профиль')),
                    desktopUserMenu))));
        }
        return (React.createElement(Space, { size: 16, align: "center" },
            modeView.showSearch && (React.createElement(Search, { ref: searchMenu, url: searchUrl, visible: visibleSearchMenuDebounced, onOpen: handleOpenSearchMenu, onClose: handleCloseMenus })),
            enableTheme && React.createElement(ThemeSwitcher, { onClick: handleThemeSwitcherClick }),
            modeView.showUser && (React.createElement(Link, __assign({}, authLoginLinkProps, { onClickCapture: handleAuthLoginClick }),
                React.createElement(Button, { size: 36, variant: fixedBackground ? "secondary" : "outlined", color: "gray" }, "\u0412\u043E\u0439\u0442\u0438")))));
    }, [
        user,
        enableTheme,
        visibleSearchMenuDebounced,
        authLoginLinkProps,
        desktopUserMenu,
        userAvatar,
        searchUrl,
        modeView,
        fixedBackground,
    ]);
    var desktopNav = React.useMemo(function () {
        return (React.createElement(React.Fragment, null,
            React.createElement(Space, { align: "center", className: cx('nav-wrapper') },
                modeView.showLogo && (React.createElement("a", { "data-element": "logo-link", href: generateUrl('/', domains.base), className: cx('logo') },
                    React.createElement(Logo, null))),
                modeView.showNavigate && (React.createElement(DesktopNavigation, { className: cx('nav'), domains: domains, settings: settings, promo: promo, onOpen: handleOpenMainMenu, onClose: handleCloseMenus, showContentWidget: showContentWidget }))),
            (modeView.showUser || modeView.showSearch || enableTheme) && (React.createElement(Space, { align: "start", inline: true }, desktopUserBlock))));
    }, [user, enableTheme, promo, settings, desktopUserBlock, modeView, domains.base]);
    React.useEffect(function () {
        var userBlock = userBlockRef.current;
        var setPosition = function () {
            if (userBlock &&
                userMenuRef.current &&
                userMenuArrowRef.current &&
                headerRef.current &&
                headerContainerRef.current) {
                var leftMargin = -20;
                var rightMargin = 16;
                var docWidth = document.documentElement.clientWidth;
                // const docWidth = headerRef.current.getBoundingClientRect().width
                var userBlockRect = userBlock.getBoundingClientRect();
                var userMenuWidth = userMenuRef.current.getBoundingClientRect().width;
                var headerWidth = headerContainerRef.current.getBoundingClientRect().width;
                var leftHeaderMargin = (docWidth - headerWidth) / 2;
                var extraWidth = userMenuWidth - userBlockRect.width + leftMargin + rightMargin;
                if (headerWidth + leftHeaderMargin + extraWidth >= docWidth) {
                    userMenuArrowRef.current.style.left = 'auto';
                    userMenuArrowRef.current.style.right = leftHeaderMargin + userBlockRect.width + leftMargin + rightMargin / 2 + "px";
                    userMenuRef.current.style.right = "-" + (docWidth - userBlockRect.right - rightMargin) + "px";
                    userMenuRef.current.style.left = 'auto';
                }
                else {
                    userMenuArrowRef.current.style.left = leftMargin * -1 + rightMargin / 2 + "px";
                    userMenuRef.current.style.right = 'auto';
                    userMenuRef.current.style.left = leftMargin + "px";
                }
            }
            pushAnalyticEvent(EVENTS.USER_MENU_SHOW, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|\u041D\u0430\u0432\u0435\u0434\u0435\u043D\u0438\u0435" });
            pushAnalyticEvent(EVENTS.DOWNLOAD_APP, { eventAction: 'Показ ссылки в Store', eventLabel: "\u041C\u0435\u043D\u044E \u043F\u0440\u043E\u0444\u0438\u043B\u044F|\u041E\u0431\u0449\u0430\u044F \u043A\u043D\u043E\u043F\u043A\u0430 \u0438 QR" });
        };
        userBlock && userBlock.addEventListener('mouseenter', setPosition);
        return function () {
            userBlock && userBlock.removeEventListener('mouseenter', setPosition);
        };
    }, [userBlockRef, userMenuRef, userMenuArrowRef, headerRef, headerContainerRef]);
    React.useEffect(function () {
        if (visibleMainMenuDebounced) {
            safeInvoke(onOpen);
        }
        else {
            !isFirstRender.current && safeInvoke(onClose);
        }
        isFirstRender.current = false;
    }, [visibleMainMenuDebounced]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: backdropClassName }),
        React.createElement("header", __assign({ ref: useMergedRef(headerRef, ref), "data-qa": "Header", className: headerClassName }, restProps),
            React.createElement(Layout, { ref: headerContainerRef },
                React.createElement(Space, { justify: "space-between", align: "center" }, desktopNav))),
        React.createElement(Divider, null),
        modeView.showUser && (React.createElement(RegionDialog, { serviceURL: serviceURL, formatPath: formatPath, regions: regions, visible: visibleRegionDialog, onClose: setVisibleRegionDialog.off, onChange: handleRegionDialogChange }))));
});
DesktopHeader.defaultProps = {
    mode: 'default',
    domains: domainsDefault,
    enableTheme: false,
    fixedBackground: false,
    showContentWidget: true
};
DesktopHeader.displayName = 'DesktopHeader';

export { DesktopHeader as default };
//# sourceMappingURL=DesktopHeader.js.map
